'use client';

import { CheckCircle, Loader2, AlertCircle, Download, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Tool UI for Export Transcript tool
 * Displays transcript export status and download button
 */
export function ExportTranscriptToolUI({ part, status }: any) {
  const result = part.result;

  if (status === 'loading' || !result) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
        <span className="text-sm text-blue-900 dark:text-blue-100">
          Exporting transcript...
        </span>
      </div>
    );
  }

  if (!result.success) {
    return (
      <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-900 dark:text-red-100">
            Failed to export transcript
          </p>
          {result.error && (
            <p className="text-xs text-red-700 dark:text-red-300 mt-1">
              {result.error}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 overflow-hidden">
      <div className="flex items-center gap-2 p-3">
        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
        <span className="text-sm font-medium text-green-900 dark:text-green-100">
          Transcript exported successfully!
        </span>
      </div>

      <div className="px-3 pb-3 space-y-3">
        <div className="flex items-center gap-3 text-xs text-green-700 dark:text-green-300">
          <div className="flex items-center gap-1">
            <FileText className="w-3 h-3" />
            <span>{result.format?.toUpperCase()}</span>
          </div>
          {result.fileSize && (
            <div>{result.fileSize}</div>
          )}
          {result.duration && (
            <div>{result.duration} duration</div>
          )}
          {result.itemCount && (
            <div>{result.itemCount} captions</div>
          )}
        </div>

        {result.fileName && (
          <div className="text-xs text-green-600 dark:text-green-400 font-mono bg-green-100 dark:bg-green-900/30 p-2 rounded">
            {result.fileName}
          </div>
        )}

        {result.downloadUrl && (
          <Button
            size="sm"
            className="w-full bg-green-600 hover:bg-green-700 text-white"
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();

              try {
                // Programmatic download to avoid navigating away from chat
                const response = await fetch(result.downloadUrl);
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = result.fileName || `transcript.${result.format}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
              } catch (error) {
                console.error('Download failed:', error);
                // Fallback: open in new tab if download fails
                window.open(result.downloadUrl, '_blank');
              }
            }}
          >
            <Download className="w-4 h-4 mr-2" />
            Download {result.format?.toUpperCase()}
          </Button>
        )}

        <p className="text-xs text-green-600 dark:text-green-400">
          💡 Free feature - download unlimited transcripts!
        </p>
      </div>
    </div>
  );
}
